// 116 — Certificado de Discapacidad / Condición Discapacitante,
// SNS-MSP/HCU-form.116/2021. NO es el carné de CONADIS (no imprime carné,
// no calcula % oficial) y NO es UCI Adulto (ese es el 113, ver
// `Formulario113.tsx` — corrección de numeración 2026-09-16 confirmada por
// fuente primaria: NO hay colisión real en el Anexo 1, el supuesto de que
// "116" se reusaba para dos formularios era un error, ver PLAN.md). El Excel
// oficial
// trae edad solo en M(eses)/A(ños), sin H/D (`soloMesesAnios`), y el pie va
// con cédula del profesional, nunca código ACESS (`etiquetaIdentificacion`).
// El campo "porcentaje" del formulario genérico anterior NO está en el
// Excel 116 — se elimina, no se conserva ni como no-oficial.
import { useMedplum } from '@medplum/react-hooks';
import type { CompositionSection, Condition } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { FormularioDiscretoProps, Par } from './index';
import { AreaDictado } from './AreaDictado';
import { CampoDictado } from './CampoDictado';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional, idTemporal } from './transaccionFhir';
import { conCodigoCie } from './camposSimplesDatos';
import {
  CLAVES_TRATAMIENTO_116,
  estadoInicial116Cert,
  faltantes116Cert,
  lineaNota116,
  lineasMarcaCuadro116,
  lineasSecuelas116,
  lineasTratamientos116,
  type ClaveTratamiento116,
  type Estado116Cert,
  type Secuela116,
  type Tratamiento116,
} from './bloques116CertDatos';
import { codeDeDiagnostico, SelectorDiagnosticoCie10, type DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';

const DIAGNOSTICO_VACIO: DiagnosticoCie10Valor = { texto: '', cie10: null };
const NUM_DIAGNOSTICOS = 6;

function narrativa(texto: string): CompositionSection['text'] {
  const escapar = (x: string): string => x.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  // Un parrafo por linea: la descripcion de un tratamiento o secuela puede traer saltos de linea.
  const parrafos = texto.split(/\r?\n/).filter((l) => l.trim() !== '').map((l) => `<p>${escapar(l)}</p>`).join('');
  return { status: 'generated', div: `<div xmlns="http://www.w3.org/1999/xhtml">${parrafos}</div>` };
}

export function Formulario116Certificado({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [estado, setEstado] = useState<Estado116Cert>(() => estadoInicial116Cert());
  const [diagnosticos, setDiagnosticos] = useState<DiagnosticoCie10Valor[]>(Array.from({ length: NUM_DIAGNOSTICOS }, () => DIAGNOSTICO_VACIO));
  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const { guardando, error, guardar } = useGuardado();
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, '116-cert', t('formularios.f116cert.titulo'), {
    etiquetaIdentificacion: t('formularios.f116cert.cedula'),
    soloMesesAnios: true,
    citaLegalCodigo: '116',
    unaHoja: true,
    // El papel (HCU-form.116, bloque A) no trae servicio ni especialidad.
    sinServicioNiEspecialidad: true,
  });

  const diagnosticosConTexto = diagnosticos.filter((d) => d.texto.trim());

  const actualizarTratamiento = (clave: ClaveTratamiento116, campo: keyof Tratamiento116, valor: string): void => {
    setEstado((prev) => ({ ...prev, tratamientos: { ...prev.tratamientos, [clave]: { ...prev.tratamientos[clave], [campo]: valor } } }));
  };
  const actualizarSecuela = (i: number, campo: keyof Secuela116, valor: string): void => {
    setEstado((prev) => ({ ...prev, secuelas: prev.secuelas.map((s, idx) => (idx === i ? { ...s, [campo]: valor } : s)) }));
  };

  const handleSubmit = (): Promise<void> => guardar(async () => {
    // Nada se descarta en silencio: si falta algo obligatorio o hay texto sin categoría, se avisa y no se guarda.
    const faltan = faltantes116Cert(estado);
    if (faltan.length > 0) throw new Error(faltan.join(' '));
    const ahora = new Date().toISOString();
    const profile = medplum.getProfile();
    const traducir = (clave: string): string => t(clave);
    const marcaCuadro = lineasMarcaCuadro116(traducir, estado);
    const tratamientos = lineasTratamientos116(traducir, estado);
    const secuelas = lineasSecuelas116(traducir, estado);
    const nota = lineaNota116(traducir);

    // Las Conditions y la Composition viajan en UNA transacción; la Composition las referencia por `urn:uuid` (el servidor lo cambia por el id real).
    const lote = new LoteTransaccional();
    const urlCondiciones: string[] = [];
    for (const d of diagnosticosConTexto) {
      const url = idTemporal();
      urlCondiciones.push(url);
      lote.crear<Condition>({ resourceType: 'Condition', subject, encounter, code: codeDeDiagnostico(d), recordedDate: ahora }, url);
    }

    const secciones: CompositionSection[] = marcaCuadro.map((l) => ({ title: l.titulo, text: narrativa(l.texto) }));
    diagnosticosConTexto.forEach((d, i) => {
      secciones.push({
        title: `${t('formularios.f116cert.diagnostico')} ${i + 1}`,
        text: narrativa(conCodigoCie(d)),
        code: codeDeDiagnostico(d),
        entry: urlCondiciones[i] ? [{ reference: urlCondiciones[i] }] : undefined,
      });
    });
    for (const l of [...tratamientos, ...secuelas]) secciones.push({ title: l.titulo, text: narrativa(l.texto) });

    lote.crear({
      resourceType: 'Composition',
      status: 'final',
      type: { text: 'Certificado de Discapacidad / Condición Discapacitante' },
      subject,
      encounter,
      date: ahora,
      author: profile ? [{ reference: `${profile.resourceType}/${profile.id}` }] : [],
      title: t('formularios.f116cert.titulo'),
      section: secciones,
    });
    await lote.enviar(medplum);

    const pares: Par[] = [
      ...marcaCuadro.map((l) => ({ pregunta: l.titulo, respuesta: l.texto })),
      ...diagnosticosConTexto.map((d, i) => ({ pregunta: `${t('formularios.f116cert.diagnostico')} ${i + 1}`, respuesta: conCodigoCie(d) })),
      ...tratamientos.map((l) => ({ pregunta: l.titulo, respuesta: l.texto })),
      ...secuelas.map((l) => ({ pregunta: l.titulo, respuesta: l.texto })),
      // La NOTA va impresa al pie del bloque F del papel.
      { pregunta: nota.titulo, respuesta: nota.texto },
    ];
    onGuardado(pares, datosEncabezadoMsp);
  });

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base font-medium text-hce-ink">{t('formularios.f116cert.titulo')}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} soloMesesAnios />

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium text-hce-ink">{t('formularios.f116cert.marca')}</legend>
        <label className="flex items-center gap-2 text-sm text-hce-ink">
          <Checkbox checked={estado.discapacidad} onCheckedChange={(v) => setEstado((p) => ({ ...p, discapacidad: v === true }))} />
          {t('formularios.f116cert.discapacidad')}
        </label>
        <label className="flex items-center gap-2 text-sm text-hce-ink">
          <Checkbox checked={estado.condicionDiscapacitante} onCheckedChange={(v) => setEstado((p) => ({ ...p, condicionDiscapacitante: v === true }))} />
          {t('formularios.f116cert.condicionDiscapacitante')}
        </label>
      </fieldset>

      <AreaDictado
        id="f116cert-cuadro-clinico"
        etiqueta={t('formularios.f116cert.cuadroClinico')}
        valor={estado.cuadroClinico}
        onChange={(v) => setEstado((p) => ({ ...p, cuadroClinico: v }))}
        filas={3}
      />

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f116cert.diagnosticos')}</h4>
      {diagnosticos.map((d, i) => (
        <SelectorDiagnosticoCie10
          key={i}
          label={`${t('formularios.f116cert.diagnostico')} ${i + 1}`}
          value={d}
          onChange={(v) => setDiagnosticos((prev) => prev.map((prev_d, idx) => (idx === i ? v : prev_d)))}
        />
      ))}

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f116cert.tratamientos')}</h4>
      {CLAVES_TRATAMIENTO_116.map((clave) => (
        <div key={clave} className="flex flex-col gap-2 rounded-md border border-border p-3">
          <p className="text-sm text-hce-ink">{t(`formularios.f116cert.tratamiento.${clave}`)}</p>
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 sm:col-span-4">
              <CampoDictado id={`f116cert-trat-${clave}-tipo`} etiqueta={t('formularios.f116cert.tratamientoTipo')} valor={estado.tratamientos[clave].tipo} onChange={(v) => actualizarTratamiento(clave, 'tipo', v)} />
            </div>
            <div className="col-span-6 sm:col-span-3">
              <Label htmlFor={`f116cert-trat-${clave}-fecha`}>{t('formularios.f116cert.tratamientoFechaInicio')}</Label>
              <Input id={`f116cert-trat-${clave}-fecha`} type="date" value={estado.tratamientos[clave].fechaInicio} onChange={(e) => actualizarTratamiento(clave, 'fechaInicio', e.target.value)} />
            </div>
            <div className="col-span-12 sm:col-span-5">
              <AreaDictado
                id={`f116cert-trat-${clave}-desc`}
                etiqueta={t('formularios.f116cert.tratamientoDescripcion')}
                valor={estado.tratamientos[clave].descripcion}
                onChange={(v) => actualizarTratamiento(clave, 'descripcion', v)}
                filas={3}
              />
            </div>
          </div>
        </div>
      ))}

      <div className="flex flex-col gap-2 rounded-md border border-border p-3">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f116cert.secuelas')}</h4>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-hce-ink">
            <Checkbox checked={estado.permanentes} onCheckedChange={(v) => setEstado((p) => ({ ...p, permanentes: v === true }))} />
            {t('formularios.f116cert.secuelaPermanentes')}
          </label>
          <label className="flex items-center gap-2 text-sm text-hce-ink">
            <Checkbox checked={estado.temporales} onCheckedChange={(v) => setEstado((p) => ({ ...p, temporales: v === true }))} />
            {t('formularios.f116cert.secuelaTemporales')}
          </label>
        </div>
        {estado.secuelas.map((s, i) => (
          <div key={i} className="grid grid-cols-12 gap-3">
            <div className="col-span-12 sm:col-span-5">
              <CampoDictado id={`f116cert-secuela-${i}-tipo`} etiqueta={`${t('formularios.f116cert.secuelaTipo')} ${i + 1}`} valor={s.tipo} onChange={(v) => actualizarSecuela(i, 'tipo', v)} />
            </div>
            <div className="col-span-12 sm:col-span-7">
              <AreaDictado
                id={`f116cert-secuela-${i}-desc`}
                etiqueta={`${t('formularios.f116cert.secuelaDescripcion')} ${i + 1}`}
                valor={s.descripcion}
                onChange={(v) => actualizarSecuela(i, 'descripcion', v)}
                filas={3}
              />
            </div>
          </div>
        ))}
        <p className="text-xs text-hce-muted">{t('formularios.f116cert.notaTexto')}</p>
      </div>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
